import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {MatSort, Sort} from '@angular/material/sort';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {TableControl} from '../../../../../common/models/TableControl';
import {Common, createFieldMap, getSortDirection} from '../../../../../common/util/Util';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {DisplayStrings} from '../../../../../common/constants/DisplayStrings';
import {PolicyProductPersonIntegrationDTO} from '../../../../models/PolicyProductPersonIntegrationDTO';
import {
  PolicyProductPersonIntegrationServiceService
} from '../../../../services/policy-product-person-integration-service.service';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';
import {SharedService} from '../../../../../common/services/shared-service';
import {detailsExpand} from '../../../../../common';
import {EntityTable} from '../../../../../common/types/entity-table.enum';

enum TableColumns {
  Created = 'created',
  IsImported = 'isImported',
  NationalIdNumber = 'nationalIdNumber',
  LastName = 'lastName',
  Gender = 'gender',
  DateOfBirth = 'dateOfBirth',
  Association = 'association',
  ProductCode = 'productCode',
  PolicyNumber = 'policyNumber',
  PolicyName = 'policyName',
  PolicyStartDate = 'policyStartDate',
  CoverStatusStartDate = 'coverStatusStartDate',
  CoverStatus = 'coverStatus'
}

@Component({
  selector: 'app-member-data-view',
  templateUrl: './member-data-view.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class MemberDataViewComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly infoTooltipImportedColumn = DisplayStrings.INFO_TOOLTIP_IMPORTED_COLUMN;
  readonly tableColumns = TableColumns;

  @ViewChild('policyProductPersonDataSort', {static: false}) policyProductPersonDataSort: MatSort;
  @ViewChild('policyProductPersonDataPaginator', {static: false}) policyProductPersonDataPaginator: MatPaginator;
  @ViewChild('policyProductPersonDataSearch') policyProductPersonDataSearch: ElementRef;

  policyProductPersonDataSearchTextChanged$ = new Subject<string>();
  policyProductPersonIdNumberSearchFormControl: FormControl;
  policyProductPersonPolicyNumberSearchFormControl: FormControl;
  policyProductPersonDataTableControl: TableControl;
  policyProductPersonDataSource: PolicyProductPersonIntegrationDTO[];
  policyProductPersonDataExpandedRow: PolicyProductPersonIntegrationDTO | null;
  policyProductPersonDataDisplayedColumns: string[] = Object.values(TableColumns);
  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  isLoading = false;
  dateFormat = DateUtil.dateFormat;

  constructor(
    private policyProductPersonIntegrationService: PolicyProductPersonIntegrationServiceService,
    private sharedService: SharedService
  ) {
    this.policyProductPersonDataTableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'created',
      'desc',
      'nationalIdNumber',
      ''
    );
  }

  ngOnInit(): void {
    this.initFormControls();
    this.initSubscriptions();
    this.getFilteredPolicyProductPersonData(this.policyProductPersonDataTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initFormControls() {
    this.policyProductPersonIdNumberSearchFormControl = new FormControl('');
    this.policyProductPersonPolicyNumberSearchFormControl = new FormControl('');
  }

  initSubscriptions() {
    this.policyProductPersonDataSearchTextChanged$.pipe(
      takeUntil(this.destroyed$),
      debounceTime(this.debounceTime),
      distinctUntilChanged()
    ).subscribe(() => {
      this.getFilteredPolicyProductPersonData(this.policyProductPersonDataTableControl);
    });
  }

  onInputChangedSearchPolicyProductPersonData($event, filterField: string): void {
    if (this.policyProductPersonDataPaginator != null) {
      this.policyProductPersonDataPaginator.firstPage();
    }
    if (filterField === TableColumns.NationalIdNumber) {
      this.policyProductPersonPolicyNumberSearchFormControl.reset();
    }
    else if (filterField === TableColumns.PolicyNumber) {
      this.policyProductPersonIdNumberSearchFormControl.reset();
    }
    this.policyProductPersonDataTableControl.filterField = filterField;
    this.policyProductPersonDataTableControl.filterValue = $event.target.value;
    this.policyProductPersonDataSearchTextChanged$.next($event.target.value);
  }

  getFilteredPolicyProductPersonData(tableControl: TableControl): void {
    this.isLoading = true;

    const pagedRequestDTO = new PagedRequestDTO();
    pagedRequestDTO.entityClass = EntityTable.TblPolicyProductPerson;
    pagedRequestDTO.pageNumber = tableControl.pageNumber;
    pagedRequestDTO.pageSize = tableControl.pageSize;
    pagedRequestDTO.sortField = tableControl.sortField;
    pagedRequestDTO.sortDirection = getSortDirection(
      tableControl.sortDirection
    );

    pagedRequestDTO.fieldMap = createFieldMap(
      tableControl.filterField,
      tableControl.filterValue
    );
    this.getFilteredPolicyProductPersonDataFromPagedRequestDTO(pagedRequestDTO);
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.policyProductPersonDataPaginator != null) {
      this.policyProductPersonDataPaginator.firstPage();
    }

    this.policyProductPersonDataTableControl.pageNumber = 0;
    this.policyProductPersonDataTableControl.sortDirection = $event.direction;
    this.policyProductPersonDataTableControl.sortField = $event.active;
    this.getFilteredPolicyProductPersonData(this.policyProductPersonDataTableControl);
  }

  pageChanged($event: PageEvent): void {
    this.policyProductPersonDataTableControl.pageNumber = $event.pageIndex;
    this.policyProductPersonDataTableControl.pageSize = $event.pageSize;
    this.getFilteredPolicyProductPersonData(this.policyProductPersonDataTableControl);
  }

  private getFilteredPolicyProductPersonDataFromPagedRequestDTO(pagedRequestDTO: PagedRequestDTO): void {
    this.policyProductPersonIntegrationService.getFilteredPolicyProductPersonData(pagedRequestDTO).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.policyProductPersonDataSource = response.resultList;
        console.log(response.resultList);
        this.policyProductPersonDataTableControl.length = response.totalRecords;
      },
      error: (errorResponse) => {
        this.isLoading = false;
        const error = errorResponse.error;
        this.sharedService.openErrorDialog(error.businessMessage);
      }
    });
  }
}
